import { FilterSettings } from '@range.io/basic-types'
import { LookupTable, mergeRight } from '@range.io/functional'
import React from 'react'
import { useSelector, useStore } from 'react-redux'
import { Checkbox, FlexColumn, FlexRow, Icon, Text, TextInput, Tooltip } from '../components-reusable/index.js'
import { styled } from '../range-theme/index.js'
import { ReduxActions, ReduxSelectors } from '../redux/index.js'
import FilterForTaskList from './FilterForTaskList.js'
import FiltersActiveBanner from './FiltersActiveBanner.js'
import { LIST_VIEW_SORT_TYPES } from './ListView.js'
import ListViewBulkActions from './ListViewBulkActions'

const ListViewHeaderWrapper = styled(FlexColumn, {
    color: '$neutral04',

    fontSize: '14px',
    lineHeight: '21px',
    borderBottom: '1px solid $neutral07',
    ai: 'center',
})

const ControlsContainer = styled(FlexRow, {
    width: '100%',
    alignItems: 'center',
    padding: '16px 16px 16px 16px',
    gap: '16px',
    height: '100%',
})

const StyledSearchInputWrapper = styled('div', {
    position: 'relative',
    width: 315,
    height: 40,
    padding: 0,

    input: {
        paddingLeft: '32px',
        fontWeight: 500,
        '&:hover': { cursor: 'pointer' },
    },
})

const StyledSearchIcon = styled(Icon, {
    position: 'absolute',
    zIndex: '1',
    top: 8,
    left: 6,
})

const StyledTableHeaderCss = styled('div', {
    ai: 'center',
    height: '32px',
    minHeight: '32px',
    width: 'stretch',
    borderTop: '1px solid $neutral07',
    background: '$neutral09',
    pl: 16,
    pr: 16,
    display: 'flex',
    alignItems: 'center',
})

const StyledTableHeaderLabel = styled(FlexRow, {
    gap: '3px',
    mr: 'auto',
    ai: 'center',
    cursor: 'pointer',
    color: '$neutral05',
    p: '4px 6px 4px 6px',
    ml: -6,
    br: 6,

    '&:hover': { color: '$neutral04', background: '$neutral07' },
})

const StyledSecondaryTableLabel = styled(Text, {
    width: 'auto',
    fs: 12,
    justifyContent: 'start',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 600,
})

const TableHeaderColumn = ({ label, onSortClick, type, sortSettings, tooltipText, width, css = {} }) => {
    const isSortedByThisColumn = !!sortSettings && sortSettings?.field === type
    const isAscending = isSortedByThisColumn && sortSettings?.isAscending

    css = mergeRight({ w: width, ml: 6, mr: 6, flex: '0 0 auto' }, css)

    const iconCss = {
        transform: !isAscending ? 'rotate(180deg)' : false,
    }

    const handleSortClick = () => onSortClick({ type, isAscending: isSortedByThisColumn ? isAscending : undefined })

    return (
        <Tooltip tooltipText={tooltipText} side="bottom" align="center">
            <StyledTableHeaderLabel data-cy={`task-list-header-button-${type}`} onClick={handleSortClick} css={css}>
                <StyledSecondaryTableLabel>{label}</StyledSecondaryTableLabel>
                {isSortedByThisColumn && <Icon css={iconCss} name="chevronDown" iconSize="16" />}
            </StyledTableHeaderLabel>
        </Tooltip>
    )
}

const EmptyCell = ({ width }) => <span style={{ width, flexGrow: 0, flexShrink: 0 }} />

const ListViewHeader = ({
    handleSearchPhraseChanged,
    checkedItems,
    clearCheckedItems,
    onDeleteItems,
    onArchiveItems,
    allAvailableAssignees,
    bulkActionSelectedAssignee,
    onBulkActionSelectedAssignee,
    areAllCheckedTasksArchived,
    areAllTasksFollowed,
    isFollowingInProgress,
    statusNames,
    bulkActionSelectedStatus,
    onBulkActionSelectedStatus,
    onBulkActionTagsChange,
    onBulkActionDateChange,
    onBulkFollowButtonClick,
    onBulkActionFollowersChange,
    showFullContent,
    allTaskNames,
    enrichedCollaborations,
    onCheckAllItemsClick,
    onSortClick,
    sortSettings,
    hasRightsToDelete,
    hasRightsToArchive,
    onExport,
    toReports,
    ...restProps
}) => {
    const { getState, dispatch } = useStore()

    const taskListFilterSettings = useSelector(ReduxSelectors.taskListFilterSettings)
    const setTaskListFilterSettings = settings => dispatch(ReduxActions.taskListFilterSettingsChanged(settings))

    const isProjectDataLoaded = ReduxSelectors.isProjectDataLoaded(getState())

    const allCanvases = useSelector(ReduxSelectors.canvasesAsLookupTable)
    const allStatuses = useSelector(ReduxSelectors.statusNamesAsLookupTable)
    const allTags = useSelector(ReduxSelectors.tagNamesAsLookupTable)
    const allParticipants = useSelector(ReduxSelectors.participantsAsLookupTable)
    const collaborations = useSelector(ReduxSelectors.collaborationsAsLookupTable)

    const selectedProject = useSelector(ReduxSelectors.selectedProject)
    const projectIdentifier = selectedProject?.identifier
    const allIdentifiers = collaborations.filter(c => !!c.isTask).flatMap(collaboration => collaboration.identifier)

    const allCategories = useSelector(ReduxSelectors.categories)

    // create a new LookupTable in canvasOrder
    const canvases = selectedProject.canvasOrder.map(c => allCanvases.get(c))
    const sortedCanvases = LookupTable(canvases, allCanvases.ItemType)

    const taskListFilterProps = {
        allCanvases: sortedCanvases,
        allStatuses,
        allTags,
        allParticipants,
        allIdentifiers,
        allCategories,
        projectIdentifier,
        filterSettings: taskListFilterSettings,
        setFilterSettings: setTaskListFilterSettings,
    }
    const isFilterApplied = FilterSettings.isSetToDefaults(taskListFilterSettings, allStatuses, allParticipants)

    const resetFilterSettings = () =>
        setTaskListFilterSettings(FilterSettings.defaultFilterSettings(allStatuses, allParticipants))

    const hasLoadedFilterSettings = Object.keys(taskListFilterSettings).length
    const areItemsPartiallyChecked = checkedItems.length && checkedItems.length !== enrichedCollaborations.length

    return (
        <ListViewHeaderWrapper {...restProps}>
            <ControlsContainer>
                <Text css={{ fontSize: 20, fontWeight: 700, marginRight: 'auto' }}>Task List</Text>

                <StyledSearchInputWrapper>
                    <StyledSearchIcon iconSize="24" css={{ color: '$neutral05' }} name="search" />
                    <TextInput
                        data-cy="list-view-search-input"
                        placeholder="Search for tasks..."
                        onChange={handleSearchPhraseChanged}
                    />
                </StyledSearchInputWrapper>

                {!!hasLoadedFilterSettings && (
                    <FilterForTaskList {...taskListFilterProps} isDataLoaded={isProjectDataLoaded} />
                )}
            </ControlsContainer>
            {isProjectDataLoaded && !isFilterApplied && (
                <FiltersActiveBanner variant="taskList" onResetClick={resetFilterSettings} />
            )}
            {!!checkedItems.length && (
                <ListViewBulkActions
                    hasRightsToArchive={hasRightsToArchive}
                    hasRightsToDelete={hasRightsToDelete}
                    checkedItems={checkedItems}
                    onClearAllClick={clearCheckedItems}
                    onDeleteItems={onDeleteItems}
                    onArchiveItems={onArchiveItems}
                    allAvailableAssignees={allAvailableAssignees}
                    bulkActionSelectedAssignee={bulkActionSelectedAssignee}
                    onBulkActionSelectedAssignee={onBulkActionSelectedAssignee}
                    areAllCheckedTasksArchived={areAllCheckedTasksArchived}
                    areAllTasksFollowed={areAllTasksFollowed}
                    isFollowingInProgress={isFollowingInProgress}
                    statusNames={statusNames}
                    bulkActionSelectedStatus={bulkActionSelectedStatus}
                    onBulkActionSelectedStatus={onBulkActionSelectedStatus}
                    onBulkActionTagsChange={onBulkActionTagsChange}
                    onBulkActionDateChange={onBulkActionDateChange}
                    onBulkFollowButtonClick={onBulkFollowButtonClick}
                    onBulkActionFollowersChange={onBulkActionFollowersChange}
                    onExport={onExport}
                    selectedProject={selectedProject}
                    toReports={toReports}
                />
            )}
            <StyledTableHeaderCss>
                <Checkbox
                    css={{ width: 12, flex: '0 0 auto', marginRight: 12 }}
                    size="md"
                    onClick={onCheckAllItemsClick}
                    checked={enrichedCollaborations.length === checkedItems.length}
                    partiallyChecked={areItemsPartiallyChecked}
                    data-cy="list-view-header-checkbox"
                />
                <EmptyCell width={20} />
                <TableHeaderColumn
                    width={356}
                    css={{ flex: '1 1 auto', minWidth: 340, ml: 6 }}
                    label={`Task Name (${enrichedCollaborations.length} total)`}
                    type={LIST_VIEW_SORT_TYPES.taskName}
                    onSortClick={onSortClick}
                    sortSettings={sortSettings}
                    tooltipText="Sort by name"
                />
                <TableHeaderColumn
                    width={88}
                    label="ID"
                    type={LIST_VIEW_SORT_TYPES.identifier}
                    onSortClick={onSortClick}
                    sortSettings={sortSettings}
                    tooltipText="Sort by identifier"
                />
                <TableHeaderColumn
                    width={168}
                    label="Assignee"
                    onSortClick={onSortClick}
                    type={LIST_VIEW_SORT_TYPES.assigneeName}
                    sortSettings={sortSettings}
                    tooltipText="Sort by assignee"
                />
                <TableHeaderColumn
                    width={168}
                    label="Category"
                    onSortClick={onSortClick}
                    type={LIST_VIEW_SORT_TYPES.categoryName}
                    sortSettings={sortSettings}
                    tooltipText="Sort by category"
                />
                <TableHeaderColumn
                    width={128}
                    label="Due Date"
                    onSortClick={onSortClick}
                    type={LIST_VIEW_SORT_TYPES.dueDate}
                    sortSettings={sortSettings}
                    tooltipText="Sort by due date"
                />
                <TableHeaderColumn
                    width={250}
                    label="Location"
                    onSortClick={onSortClick}
                    type={LIST_VIEW_SORT_TYPES.locationName}
                    sortSettings={sortSettings}
                    tooltipText="Sort by location"
                />
                {showFullContent && (
                    <>
                        <EmptyCell width={48} />
                        <EmptyCell width={38} />
                        <TableHeaderColumn
                            width={100}
                            label="Created On"
                            onSortClick={onSortClick}
                            type={LIST_VIEW_SORT_TYPES.creationDate}
                            sortSettings={sortSettings}
                            tooltipText="Sort by created on date"
                        />
                    </>
                )}
            </StyledTableHeaderCss>
        </ListViewHeaderWrapper>
    )
}

export default ListViewHeader
